import React, { useMemo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import GlyphChart from '../GlyphChart/GlyphChart';
import Label from '../Label/Label';
import XRayChart from '../XRayChart/XRayChart';
import Modal from '../Modal/Modal';
import getInitialLetters from '../../utils/getInitialLetters';
import translate from '../../libs/i18n';
import { examShape } from '../../utils/propTypesShapes';
import toTitleCase from '../../utils/toTitleCase';
import ActionButton from '../ActionButton/ActionButton';
import CheckIcon from '../../assets/svgs/CheckIcon';
import DeleteReportIcon from '../../assets/svgs/DeleteReportIcon';
import { useDisableBodyScroll } from '../../utils/useDisableBodyScroll';
import { functions, colors } from '../../ui/variables';

import formatDate from '../../utils/formatDate';
import WebVisualizerDensitometry from '../../pages/WebVisualizerDensitometry/WebVisualizerDensitometry';
import WebVisualizerCNN from '../../pages/WebVisualizerCNN/WebVisualizerCNN';


import { ExamReportItemStyle } from './ExamReportItemStyle';

const ExamReportItem = ({ exam, onDeleteReport }) => {

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [densitometryReportModal, setDensitometryReportModal] = useState(false);
  const [CNNReportModal, setCNNReportModal] = useState(false);

  const language = useSelector((state) => state.settings.language);
  const handleCloseModal = () => setShowDeleteModal(false);

  const onlyDensitometry = useMemo(
    () => process.env.REACT_APP_ONLY_DENSITOMETRY === 'True',
    []
  );

  useDisableBodyScroll(densitometryReportModal);

  const isRadiography = useMemo(
    () =>
      exam &&
      exam.type &&
      exam.type.length &&
      ['CR', 'DX', 'IO'].includes(exam.type.toUpperCase()),
    [exam]
  );

  const getExamDate = useCallback((date) => {
    if (date && date.length) {
      const dateObj = date.replaceAll('-', '');
      return formatDate(dateObj, language === 'pt-br' ? 'dd/mm/yyyy' : 'mm/dd/yyyy');
    }
    return '';
  }, [language]);
  
  
  return (
    <ExamReportItemStyle key={exam.id}>
      {showDeleteModal ? (
        <Modal theme="light" handleClose={() => setShowDeleteModal(false)}>
          <div className="confirm-report-modal-container">
            <h3 className="report-modal-title">
              {toTitleCase(translate('deleteexam', language))}
            </h3>
            <p className="report-modal-question">
              {translate('areYouSureDeleteExam', language)}
            </p>
            <p className="report-modal-description">
              {translate('afterConfirmationTheExamDeleted', language)}
            </p>
            <div className="report-modal-actions">
              <div className="modal-action-wrapper">
                <ActionButton
                  theme="light-disabled"
                  text={translate('cancel', language).toUpperCase()}
                  onClick={() => setShowDeleteModal(false)}
                />
              </div>
              <div className="modal-action-wrapper">
                <ActionButton
                  theme="default"
                  text={translate('yesIDo', language).toUpperCase()}
                  onClick={() => {
                    setShowDeleteModal(false);
                    onDeleteReport(exam.id, exam.study_id);

                  }}
                />
              </div>
            </div>
          </div>
        </Modal>
      ) : null}

      {densitometryReportModal ? (
        <Modal
          overlay={`${functions.rgba(colors.black, 0.7)}`}
          theme="dark"
          fullScreen
          handleClose={() => setDensitometryReportModal(false)}
        >
          <WebVisualizerDensitometry modal={true} report={exam.report} pdf={exam.report.pdf_densitometry}/>
        </Modal>
      ) : null}

      {CNNReportModal ? (
        <Modal
          overlay={`${functions.rgba(colors.black, 0.7)}`}
          theme="dark"
          fullScreen
          handleClose={() => setCNNReportModal(false)}
        >
          <WebVisualizerCNN modal={true} report={exam.report} pdf={exam.report.pdf_densitometry}/>
        </Modal>
      ) : null}

      <div className="exam-report-item-container">
        { onlyDensitometry ? (
              <>
                <button
                  className={`exam-report-link${exam.status && exam.status !== 'ERR' ? ' with-report' : ''}`}
                  type="button"
                  onClick={() => { !isRadiography ? (
                    setDensitometryReportModal(true)
                  ) : setCNNReportModal(true)}}
                >
                  <div className="glyph-container">
                    {exam &&
                    exam.report &&
                    (exam.report.glyph ||
                      (exam.report.probability !== null &&
                        exam.report.probability !== undefined)) ? (
                      <>
                        {exam.report.glyph && !isRadiography ? (
                          <GlyphChart glyph={exam.report.glyph} chartOnly />
                        ) : null}
                        {exam.report.probability !== null &&
                        exam.report.probability !== undefined &&
                        isRadiography ? (
                          <XRayChart
                            percent={exam.report.probability}
                            classification={exam.report.classification}
                            chartOnly
                          />
                        ) : null}
                      </>
                    ) : (
                      <div className="glyph-placeholder">
                        <span className="glyph-placeholder-content">
                          {getInitialLetters(
                            exam && exam.patient && exam.patient.name
                              ? exam.patient.name
                              : ''
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
                <button
                  className={`exam-report-information${
                    exam.status && exam.status != 'ERR' ? ' with-report' : ''
                  }`}
                  type="button"
                  onClick={() => { exam?.status && exam.status !== 'ERR' ? (
                    !isRadiography ? (
                    setDensitometryReportModal(true)
                    ): setCNNReportModal(true)
                  ) : null}}
                >
                  <div className="exam-report-type">
                    {exam?.type?.length
                      ? translate(exam.type.toLowerCase(), language)
                      : ''}
                  </div>
                  <div className="exam-report-medical-registry">
                    {exam?.registry ? exam.registry : null}
                  </div>
                  <div className="exam-report-patient-name">
                    {exam?.patient?.name ? exam.patient.name : ''}
                  </div>
                  <div className="exam-report-date">
                    {exam?.date_requested ? getExamDate(exam.date_requested) : ''}
                  </div>
                  <div className="exam-report-date-registration">
                    {exam?.date_created ? getExamDate(exam.date_created) : ''}
                  </div>
                  <div className="exam-report-doctor-name">
                    {exam?.doctor?.name ? exam.doctor.name : null}
                  </div>
                  <div className="exam-report-status">
                    <Label
                      theme={
                        exam && exam.status && exam.status.length
                          ? exam.status
                          : 'default'
                      }
                      text={
                        exam && exam.status && exam.status.length
                          ? translate(exam.status.toLowerCase(), language)
                          : `${translate('processing', language)}…`
                      }
                      icon={
                        exam && exam.status && exam.status === 'APP' ? (
                          <CheckIcon />
                        ): null
                      }
                    />
                  </div>
                </button>
              </>
        ) : 
        <>  
          <Link
            className={`exam-report-link${exam.status && exam.status != 'ERR' ? ' with-report' : ''}`}
            // to={exam.report?.id ? `/relatorios/${exam.report.id}` : ''}
            to={
              exam?.status && exam.status !== 'ERR'
                ? `/relatorios/${
                    isRadiography
                      ? 'radiografias'
                      : 'tomografias'
                  }/${exam.report.id}`
                : ''
            }
          >
          <div className="glyph-container">
            {exam &&
            exam.report &&
            (exam.report.glyph ||
              (exam.report.probability !== null &&
                exam.report.probability !== undefined)) ? (
              <>
                {exam.report.glyph && !isRadiography ? (
                  <GlyphChart glyph={exam.report.glyph} chartOnly />
                ) : null}
                {exam.report.probability !== null &&
                exam.report.probability !== undefined &&
                isRadiography ? (                  
                  <XRayChart percent={exam.report.probability} classification={exam.report.classification} chartOnly />
                ) : null}
              </>
            ) : (
              <div className="glyph-placeholder">
                <span className="glyph-placeholder-content">
                  {getInitialLetters(
                    exam && exam.patient && exam.patient.name
                      ? exam.patient.name
                      : ''
                  )}
                </span>
              </div>
            )}
          </div>
          </Link>
          <Link
            className={`exam-report-information${
              exam.status && exam.status != 'ERR' ? ' with-report' : ''
            }`}
            to={
              exam?.status && exam.status !== 'ERR'
                ? `/relatorios/${
                  isRadiography
                      ? 'radiografias'
                      : 'tomografias'
                  }/${exam.report.id}`
                : ''
            }
          >
            <div className="exam-report-type">
              {exam?.type?.length
                ? translate(exam.type.toLowerCase(), language)
                : ''}
            </div>
            <div className="exam-report-medical-registry">
              {exam?.registry ? exam.registry : null}
            </div>
            <div className="exam-report-patient-name">
              {exam?.patient?.name ? exam.patient.name : ''}
            </div>
            <div className="exam-report-date">
              {exam?.date_requested ? getExamDate(exam.date_requested) : ''}
            </div>
            <div className="exam-report-date-registration">
              {exam?.date_created ? getExamDate(exam.date_created) : ''}
            </div>
            <div className="exam-report-doctor-name">
              {exam?.doctor?.name ? exam.doctor.name : null}
            </div>
            <div className="exam-report-status">
              <Label
                theme={
                  exam && exam.status && exam.status.length
                    ? exam.status
                    : 'default'
                }
                text={
                  exam && exam.status && exam.status.length
                    ? translate(exam.status.toLowerCase(), language)
                    : `${translate('processing', language)}…`
                }
                icon={
                  exam && exam.status && exam.status === 'APP' ? (
                    <CheckIcon />
                  ): null
                }
              />
            </div>
          </Link> 
        </> } 
        {/* {exam && exam.report && exam.report.id ? (
          <div className="exam-report-go-to">
            <Link to={`/relatorios/${exam.report.id}`}>
              <span className="go-to-report-text">
                {translate('seeMore', language)}
              </span>
            </Link>
          </div>
        ) : null} */}
        <div className="action-buttons-mobile">
          <button
            type="button"
            className="action-button-with-icon-mobile"
        onClick={() => setShowDeleteModal(true)/*onDeleteReport(exam.id, exam.study_id)*/}
          >
            <DeleteReportIcon />
          </button>
        </div>

        <div className="exam-report-actions">
          <button
            type="button"
            className="action-button"
            onClick={() => setShowDeleteModal(true)/*onDeleteReport(exam.id, exam.study_id)*/}
          >
            <DeleteReportIcon />
          </button>
        </div>
      </div>
    </ExamReportItemStyle>
  );
};

ExamReportItem.propTypes = {
  exam: examShape,
  onDeleteReport: PropTypes.func.isRequired,
};

ExamReportItem.defaultProps = {
  exam: {},
};

export default ExamReportItem;
